import type { VisibilityReportModel } from '@brand24-original/llm-types';
import { ChatType } from '../../../shared/types/monitor/ChatType';

/**
 * ChatType z filtrów panelu → rodzina modelu w kontrakcie raportu. Lustro `mapChatTypeToModelName`
 * z llm-common; backend rozwija rodzinę z powrotem na chat types, więc obie strony muszą nazywać ją
 * tak samo (`AI_OVERVIEW` to nowe AI Overview, `AI_MODE` to AI Mode).
 */
const CHAT_TYPE_TO_MODEL: Partial<Record<ChatType, VisibilityReportModel>> = {
  GPT: 'GPT',
  GPT_BROWSER: 'GPT',
  GPT_SEARCH: 'GPT',
  GEMINI: 'GEMINI',
  GEMINI_BROWSER: 'GEMINI',
  CLAUDE: 'CLAUDE',
  DEEP_SEEK: 'DEEP_SEEK',
  PERPLEXITY: 'PERPLEXITY',
  PERPLEXITY_BROWSER: 'PERPLEXITY',
  GROK: 'GROK',
  GROK_BROWSER: 'GROK',
  COPILOT_BROWSER: 'COPILOT',
  AI_OVERVIEW_NEW_BROWSER: 'AI_OVERVIEW',
  AI_MODE_BROWSER: 'AI_MODE',
  AI_OVERVIEW_BROWSER: 'AI_MODE',
};

/**
 * Klucz modelu ze statystyk (`GET /model-data-date-ranges`, zwijany w SQL-u) → ChatType z filtrów.
 * To INNY słownik niż rodziny kontraktu: statystyki oddają nowe AI Overview jako `AI_OVERVIEW_NEW`.
 */
const STATS_MODEL_TO_CHAT_TYPE: Record<string, ChatType> = {
  GPT: 'GPT',
  GEMINI: 'GEMINI',
  CLAUDE: 'CLAUDE',
  DEEP_SEEK: 'DEEP_SEEK',
  PERPLEXITY: 'PERPLEXITY',
  GROK: 'GROK_BROWSER',
  COPILOT: 'COPILOT_BROWSER',
  AI_OVERVIEW_NEW: 'AI_OVERVIEW_NEW_BROWSER',
  AI_MODE: 'AI_MODE_BROWSER',
};

/** Modele, które w danym zakresie faktycznie coś zebrały — przecięte z tym, co konto ma włączone. */
export const chatTypesWithData = (
  statsModels: string[],
  availableTypes: ChatType[]
): ChatType[] => {
  const withData = new Set(
    statsModels.map((model) => STATS_MODEL_TO_CHAT_TYPE[model]).filter(Boolean)
  );

  return availableTypes.filter((type) => withData.has(type));
};

/**
 * Zaznaczone modele → filtr raportu. Komplet modeli konta oddajemy jako PUSTĄ listę: raport ma wtedy
 * objąć wszystko, co projekt ma w danych, także modele odpytywane historycznie, a których konto już
 * nie ma w uprawnieniach.
 */
export const chatTypesToReportModels = (
  selected: ChatType[],
  availableTypes: ChatType[]
): VisibilityReportModel[] => {
  if (selected.length >= availableTypes.length) {
    return [];
  }

  const models = selected
    .map((chatType) => CHAT_TYPE_TO_MODEL[chatType])
    .filter((model): model is VisibilityReportModel => !!model);

  // Kilka chat typów zwija się do jednej rodziny (np. GPT + GPT_BROWSER) — duplikat wywaliłby DTO
  // na `@ArrayUnique`.
  return models.filter((model, index) => models.indexOf(model) === index);
};
